import { ApiError } from "@carepass/api-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { downloadAdminFile } from "../../app/files";
import { useAdminNavigation } from "../../app/navigation/useAdminNavigation";
import { useAdminApplication, useAdminSession, useAdminUiStore } from "../../app/providers";
import { parseAdminEntityId, pathForAdminEntityScreen } from "../../app/routes";
import { failure } from "../workspace/behavior";
import type { Schemas } from "../workspace/api.generated";
import type { Command } from "../workspace/model";
import { StateNotice } from "../workspace/shared";
import { InquiryView } from "./components/InquiryView";
import type { InquiryAttachmentPreview } from "./components/InquiryView";
import { useInquiryDetail, useInquiryMutation } from "./queries";

function queryState(query: ReturnType<typeof useInquiryDetail>) {
  if (query.isPending) return "loading" as const;
  if (query.error instanceof ApiError && query.error.kind === "forbidden") return "denied" as const;
  if (query.error instanceof ApiError && query.error.kind === "not-found") return "not-found" as const;
  if (query.isError) return "error" as const;
  return "ready" as const;
}

function unresolvedInquiryId(
  commands: Readonly<Record<string, { readonly startedAt: string }>>,
): number | undefined {
  const entry = Object.entries(commands)
    .filter(([key]) => key.startsWith("inquiry-command:"))
    .sort(([, left], [, right]) => right.startedAt.localeCompare(left.startedAt))[0];
  const parsed = entry ? Number(entry[0].slice("inquiry-command:".length)) : Number.NaN;
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function InquiryDetailPageForInquiry({ inquiryId }: { readonly inquiryId: number }) {
  const services = useAdminApplication();
  const session = useAdminSession();
  const commands = useAdminUiStore((state) => state.unresolvedCommands);
  const markCommandUnresolved = useAdminUiStore((state) => state.markCommandUnresolved);
  const resolveCommand = useAdminUiStore((state) => state.resolveCommand);
  const unresolvedId = useMemo(() => unresolvedInquiryId(commands), [commands]);
  const detail = useInquiryDetail(inquiryId);
  const mutation = useInquiryMutation();
  const navigation = useAdminNavigation({
    contextKey: "inquiries",
    defaultListPath: "/inquiries?page=0&size=20",
    originLabel: "문의 상세",
  });
  const [notice, setNotice] = useState("");
  const [recoveryError, setRecoveryError] = useState("");
  const fileRequests = useRef(new Set<AbortController>());
  const authorities = session.authSession?.authorities ?? [];

  useEffect(() => () => {
    for (const controller of fileRequests.current) controller.abort();
    fileRequests.current.clear();
  }, []);

  const refresh = async () => {
    setRecoveryError("");
    const result = await detail.refetch();
    if (result.error) throw result.error;
    if (unresolvedId === inquiryId) resolveCommand(`inquiry-command:${inquiryId}`);
    return result.data;
  };

  const runCommand = async (command: Command) => {
    const commandKey = `inquiry-command:${inquiryId}`;
    markCommandUnresolved(commandKey, {
      operation: command.operation,
      targetId: String(inquiryId),
      startedAt: new Date().toISOString(),
    });
    try {
      if (command.operation === "POST /api/v1/admin/inquiries/{inquiryId}/assignment") {
        return await mutation.mutateAsync({ kind: "assign", inquiryId });
      }
      if (command.operation === "POST /api/v1/admin/inquiries/{inquiryId}/replies") {
        return await mutation.mutateAsync({ kind: "reply", inquiryId, body: command.body });
      }
      resolveCommand(commandKey);
      throw new Error(`Unsupported inquiry command: ${command.operation}`);
    } catch (cause) {
      if (!failure(cause).reconcile) resolveCommand(commandKey);
      throw cause;
    }
  };

  const commandComplete = async (updated: Schemas["InquiryView"]) => {
    resolveCommand(`inquiry-command:${inquiryId}`);
    setNotice(`${updated.inquiryNo} 처리 결과가 ${updated.status} 상태로 반영되었습니다.`);
    const result = await detail.refetch();
    if (result.error) {
      setRecoveryError("문의 처리 결과는 확인했지만 상세를 갱신하지 못했습니다. 최신 상태를 다시 조회해 주세요.");
      throw result.error;
    }
  };

  const downloadAttachment = async (fileAssetId: number): Promise<InquiryAttachmentPreview> => {
    const request = new AbortController();
    fileRequests.current.add(request);
    try {
      const blob = await downloadAdminFile(services, fileAssetId, {
        businessRefType: "INQUIRY",
        businessRefId: inquiryId,
      }, request.signal);
      return {
        url: services.sessionLifecycle.createPreviewUrl(blob),
        contentType: blob.type || "application/octet-stream",
      };
    } finally {
      fileRequests.current.delete(request);
    }
  };

  const releasePreview = useCallback((url: string) => {
    services.sessionLifecycle.revokePreviewUrl(url);
  }, [services.sessionLifecycle]);

  const memberId = detail.data?.memberId;
  const memberHref = memberId && authorities.includes("MEMBER_READ")
    ? pathForAdminEntityScreen("06", memberId)
    : undefined;

  return (
    <InquiryView
      mode="detail"
      filters={{ status: "", memberId: "", assignedAdminLoginId: "", from: "", to: "" }}
      selected={detail.data}
      state={queryState(detail)}
      actorId={session.actor?.id ?? 0}
      canManage={authorities.includes("INQUIRY_MANAGE")}
      unresolvedInquiryId={unresolvedId === inquiryId ? unresolvedId : undefined}
      notice={notice}
      recoveryError={recoveryError}
      backHref={navigation.backPath}
      backLabel={navigation.backLabel}
      onBack={navigation.backToList}
      detailHref={(inquiry) => pathForAdminEntityScreen("25", inquiry.id)}
      memberHref={memberHref}
      memberAccessDenied={!authorities.includes("MEMBER_READ")}
      onOpenMember={memberHref ? () => navigation.openRelated(memberHref, "문의 상세") : undefined}
      onSearch={() => undefined}
      onPageChange={() => undefined}
      onSelect={() => undefined}
      onRetry={() => { void detail.refetch(); }}
      onRefresh={refresh}
      onCommand={runCommand}
      onCommandComplete={commandComplete}
      onDownloadAttachment={downloadAttachment}
      onReleasePreview={releasePreview}
    />
  );
}

export function InquiryDetailPage() {
  const inquiryId = parseAdminEntityId(useParams().inquiryId);
  if (inquiryId === null) return <StateNotice state="not-found" />;
  return <InquiryDetailPageForInquiry key={inquiryId} inquiryId={inquiryId} />;
}
